import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hapticImpact } from "@/lib/haptics";

const homeFor = (role) =>
  role === "super_admin" ? "/admin" : role === "nutritionist" || role === "coach" ? "/nutrizionista" : "/app";

const THRESHOLD = 90;
const snapshots = new Map(); // history idx → { node, scrollY }

// Skip the gesture when the touch starts inside horizontally scrollable
// content, form fields, or anything explicitly marked data-no-swipe.
function shouldIgnoreTarget(el) {
  let node = el;
  while (node && node !== document.body) {
    if (node.dataset && node.dataset.noSwipe !== undefined) return true;
    const tag = node.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if (node.scrollWidth > node.clientWidth + 5) {
      const ox = window.getComputedStyle(node).overflowX;
      if (ox === "auto" || ox === "scroll") return true;
    }
    node = node.parentElement;
  }
  return false;
}

const openDialog = () =>
  document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]');
const pageEl = () => document.querySelector("main:not([data-swipe-snapshot])");
const histIdx = () => window.history.state?.idx ?? 0;

function takeSnapshot() {
  const m = pageEl();
  if (!m) return null;
  const node = m.cloneNode(true);
  node.querySelectorAll("[data-testid]").forEach((n) => n.removeAttribute("data-testid"));
  node.querySelectorAll("video, iframe, canvas").forEach((n) => n.remove());
  node.setAttribute("aria-hidden", "true");
  node.setAttribute("data-swipe-snapshot", "1");
  node.style.pointerEvents = "none";
  return { node, scrollY: window.scrollY };
}

/**
 * SwipeBack — global iOS-style "swipe right → go back" gesture with live
 * preview: the current page slides with the finger and a snapshot of the
 * previous page is revealed underneath. Open sheet/dialog → closes it.
 */
export default function SwipeHome() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef({});
  const hintRef = useRef(null);
  const pendingRef = useRef(null);
  ref.current = { user, pathname: location.pathname, navigate };

  // Keep the most recent pre-navigation snapshot; file it under the index we're leaving.
  const lastIdx = useRef(histIdx());
  useEffect(() => {
    const prev = lastIdx.current;
    const now = histIdx();
    if (now > prev && pendingRef.current) snapshots.set(prev, pendingRef.current);
    for (const k of snapshots.keys()) if (k >= now) snapshots.delete(k);
    lastIdx.current = now;
    pendingRef.current = null;
  }, [location]);

  useEffect(() => {
    let start = null, sliding = false, animating = false, under = null;
    const hint = hintRef.current;

    const onPreNav = () => { if (!openDialog()) pendingRef.current = takeSnapshot(); };
    document.addEventListener("pointerdown", onPreNav, { capture: true, passive: true });

    const setSlide = (dx, instant) => {
      const m = pageEl();
      if (!m) return;
      const w = window.innerWidth;
      const p = Math.min(1, dx / w);
      if (!m.style.zIndex) {
        const bg = getComputedStyle(m.parentElement || document.body).backgroundColor;
        m.style.position = "relative"; m.style.zIndex = "1";
        m.style.backgroundColor = bg && bg !== "rgba(0, 0, 0, 0)" ? bg : getComputedStyle(document.body).backgroundColor;
        if (under) under.style.backgroundColor = m.style.backgroundColor;
      }
      m.style.transition = instant ? "none" : "transform 260ms cubic-bezier(.2,.8,.2,1)";
      m.style.transform = `translate3d(${dx}px,0,0)`;
      m.style.boxShadow = dx > 0 ? "-12px 0 32px rgba(0,0,0,0.28)" : "";
      if (under) {
        under.style.transition = instant ? "none" : "opacity 260ms, transform 260ms cubic-bezier(.2,.8,.2,1)";
        under.style.opacity = "1";
        under.style.transform = `translate3d(${Math.round(-w * 0.3 * (1 - p))}px,0,0)`;
        under.firstElementChild?.style.setProperty("filter", `brightness(${0.6 + p * 0.4})`);
      }
    };
    const resetSlide = () => {
      const m = pageEl();
      if (m) { m.style.transition = ""; m.style.transform = ""; m.style.boxShadow = ""; m.style.position = ""; m.style.zIndex = ""; m.style.backgroundColor = ""; }
      if (under) { under.remove(); under = null; }
      sliding = false;
    };
    const mountUnder = () => {
      const snap = snapshots.get(histIdx() - 1);
      const m = pageEl();
      if (!snap || !m || !m.parentElement) return false;
      under = document.createElement("div");
      under.setAttribute("data-testid", "swipe-back-preview");
      under.setAttribute("aria-hidden", "true");
      under.style.cssText = "position:fixed;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:0;";
      const wrap = document.createElement("div");
      wrap.style.cssText = `position:absolute;left:0;right:0;top:${m.getBoundingClientRect().top + window.scrollY - snap.scrollY}px;`;
      wrap.appendChild(snap.node.cloneNode(true));
      under.appendChild(wrap);
      m.parentElement.insertBefore(under, m);
      return true;
    };

    const showHint = (dx) => {
      if (!hint) return;
      const p = Math.min(1, Math.max(0, (dx - 20) / (THRESHOLD - 20)));
      hint.style.opacity = String(p);
      hint.style.transform = `translate(${Math.round(p * 28) - 44}px, -50%) scale(${0.7 + p * 0.3})`;
      hint.dataset.armed = p >= 1 ? "1" : "0";
    };
    const hideHint = () => { if (hint) { hint.style.opacity = "0"; hint.style.transform = "translate(-44px, -50%) scale(0.7)"; } };

    const onStart = (e) => {
      start = null;
      if (animating) return;
      const { user } = ref.current;
      if (!user?.role) return;
      if (e.touches.length !== 1) return;
      if (shouldIgnoreTarget(e.target)) return;
      const t = e.touches[0];
      start = { x: t.clientX, y: t.clientY, ts: Date.now(), dialog: !!openDialog(), canSlide: !openDialog() && histIdx() > 0 };
    };

    const onMove = (e) => {
      if (!start) return;
      const t = e.touches[0];
      const dx = t.clientX - start.x, dy = t.clientY - start.y;
      if (!sliding && (Math.abs(dy) > 70 || dx < 0)) { start = null; hideHint(); return; }
      if (dx > 20 && dx > Math.abs(dy) * 1.5) {
        if (start.dialog) { showHint(dx); return; }
        if (start.canSlide && !sliding) { sliding = mountUnder(); if (!sliding) showHint(dx); }
        if (sliding) setSlide(dx, true); else showHint(dx);
      }
    };

    const finish = (go) => {
      const { navigate } = ref.current;
      animating = true;
      const w = window.innerWidth;
      setSlide(go ? w : 0, false);
      window.setTimeout(() => {
        if (go) navigate(-1);
        // Give the previous page one frame to render under the snapshot, then clean up.
        window.setTimeout(() => { resetSlide(); animating = false; }, go ? 80 : 0);
      }, 260);
    };

    const onEnd = (e) => {
      hideHint();
      if (!start) return;
      const t = e.changedTouches && e.changedTouches[0];
      const s = start; start = null;
      if (!t) { if (sliding) finish(false); return; }
      const dx = t.clientX - s.x, dy = t.clientY - s.y, dt = Date.now() - s.ts;
      const ok = dt <= 900 && dx >= THRESHOLD && Math.abs(dy) <= 70 && dx >= Math.abs(dy) * 2;
      const flick = dt < 250 && dx > 40;
      if (sliding) {
        const go = ok || flick || dx > window.innerWidth * 0.4;
        if (go) hapticImpact("medium");
        finish(go);
        return;
      }
      if (!ok) return;
      const { user, pathname, navigate } = ref.current;
      hapticImpact("medium");
      if (s.dialog || openDialog()) {
        document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", code: "Escape", bubbles: true }));
        return;
      }
      const home = homeFor(user.role);
      if (histIdx() > 0) navigate(-1);
      else if (pathname !== home) navigate(home);
    };

    const onCancel = () => { start = null; hideHint(); if (sliding) finish(false); };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    document.addEventListener("touchcancel", onCancel, { passive: true });
    return () => {
      document.removeEventListener("pointerdown", onPreNav, { capture: true });
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onCancel);
      resetSlide();
    };
  }, []);

  return (
    <>
      <div
        ref={hintRef}
        aria-hidden
        data-testid="swipe-back-hint"
        className="pointer-events-none fixed left-0 top-1/2 z-[9999] h-11 w-11 rounded-full bg-[#1A1D1E]/85 dark:bg-white/90 text-white dark:text-[#1A1D1E] shadow-[0_8px_24px_-6px_rgba(0,0,0,0.5)] backdrop-blur flex items-center justify-center data-[armed=1]:bg-[#5A8B8F] dark:data-[armed=1]:bg-[#5A8B8F] data-[armed=1]:text-white"
        style={{ opacity: 0, transform: "translate(-44px, -50%) scale(0.7)", transition: "background-color 150ms" }}
      >
        <ChevronLeft className="h-6 w-6" />
      </div>
    </>
  );
}
